import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { CacheBucket, HttpCacheManager, withCache } from "@ngneat/cashew";
import { Utils } from "@serasa/shared/utils/utils";
import { BehaviorSubject, Observable, forkJoin, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { IMyAudit } from "@serasa/core/pages/actions-history/models/deleted-user.interface";
import { environment } from "../../../../../environments/environment";
import { UserMe } from "../../recertification/interfaces/user-me.interface";
import { AccountResponse } from "../interfaces/account-response.interface";
import {
  CloneLogonBody,
  CloneLogonRequest,
  CloneLogonResponse,
  CloneLogonResult,
} from "../interfaces/clone-logon.interface";
import { CloneLogonSearchItem } from "../interfaces/clone-logon-search-item.interface";
import { Logon } from "../interfaces/logon.interface";
import { IUserAccountUpdatePayload } from "../interfaces/payload-update-user-account.interface";
import { IUserProfileUpdatePayload } from "../interfaces/payload-update-user-profile.interface";
import { ResetUser } from "../interfaces/reset-user.interface";
import { User } from "../interfaces/user.interface";
import { CloneLogonSearchResponse } from "../interfaces/cloneable-logon-search.interface";
import {
  BackendAccessType,
  mapIsSystemicFromAccessTypes,
} from "@serasa/shared/utils/account-guardrails.util";

type RawLogonItem = User & {
  accessTypes?: Array<BackendAccessType> | null;
};

@Injectable({ providedIn: "root" })
export class UserService {
  public currentUser$ = new BehaviorSubject<Partial<User>>({});
  public updated$ = new BehaviorSubject<Partial<boolean>>(false);
  public accessToken$ = new BehaviorSubject("");

  private logonsBucket = new CacheBucket();

  constructor(
    private readonly _http: HttpClient,
    public bucket: HttpCacheManager,
    @Inject('CACHE_EXPIRATION') private cacheExpiration: number
  ) { }

  public resetPassword(user: ResetUser) {
    const headers = Utils.generateDefaultHeader();

    return this._http.post(
      `${environment.logonPortalBaseUrl}/user-identities/reset-password`,
      user,
      { headers },
    );
  }

  public getCloneableLogonSearchData(
    logon: string,
  ): Observable<CloneLogonSearchResponse> {
    const headers = Utils.generateDefaultHeader();

    return this._http.get<CloneLogonSearchResponse>(
      `${environment.logonPortalBaseUrl}/my-logons/${logon}/clone-data`,
      { headers },
    );
  }

  public cloneLogon(
    request: CloneLogonRequest,
  ): Observable<CloneLogonResult[]> {
    const headers = Utils.generateDefaultHeader();
    const entries = request.entries ?? [];

    const requests = entries.map((entry) => {
      const email = entry.email.trim();
      const providedName = entry.name?.trim();
      const name = providedName || email.split("@")[0].slice(0, 20);
      const body: CloneLogonBody = { email, name };
      return this._http
        .post<CloneLogonResponse>(
          `${environment.logonPortalBaseUrl}/my-logons/${request.logon}/clone`,
          body,
          { headers },
        )
        .pipe(
          map((response) => ({ email, name, response }) as CloneLogonResult),
          catchError((error: HttpErrorResponse) =>
            of({ email, name, error } as CloneLogonResult),
          ),
        );
    });

    return forkJoin(requests);
  }

  public getUserAccount(logon: string): Observable<Logon> {
    const headers = Utils.generateDefaultHeader();
    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `my-logons-${logon}`,
      bucket: this.logonsBucket,
    };

    return this._http
      .get<Logon & { accessTypes?: Array<BackendAccessType> | null }>(
        `${environment.logonPortalBaseUrl}/my-logons/${logon}`,
        {
          headers,
          context: withCache(cacheOptions),
        },
      )
      .pipe(
        map((raw) => ({
          ...raw,
          isSystemic: mapIsSystemicFromAccessTypes(raw.accessTypes),
        })),
      );
  }

  public updateUserAccount(
    userId: string,
    logon: string,
    payload: IUserAccountUpdatePayload,
  ): Observable<unknown> {
    const defaultHeader = Utils.generateDefaultHeader();
    const headers = defaultHeader.append("Invalidate-Cache", "true");
    const params = new HttpParams({ fromObject: { logon } });

    return this._http.patch<unknown>(
      `${environment.logonPortalBaseUrl}/user-accounts/${userId}`,
      { ...payload },
      {
        headers,
        params,
      },
    );
  }

  public updateUserProfile(
    userId: string,
    profile: string,
  ): Observable<unknown> {
    const headers = Utils.generateDefaultHeader();

    const payload: IUserProfileUpdatePayload = { profile };

    return this._http
      .patch<unknown>(
        `${environment.logonPortalBaseUrl}/my-logons/${userId}/profile`,
        payload,
        {
          headers,
        },
      )
      .pipe(
        tap({
          next: (response: unknown) => {
            this.invalidateLogonsCache();
            this.updated$.next(true);
          },
          error: (error: unknown) => {
            console.error("UserService - Erro na requisição:", error);
          },
        }),
      );
  }

  public getAllLogons(
    offset?: number,
    pageSize?: number,
  ): Observable<AccountResponse> {
    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader();

    const params = new HttpParams({
      fromObject: {
        offset: offset ?? 0,
        limit: pageSize ?? 20,
      },
    });

    const cacheOptions = {
      strategy: "implicit",
      ttl: this.cacheExpiration,
      key: `logons-${offset}`,
      bucket: this.logonsBucket,
    };

    return this._http
      .get<AccountResponse>(url, {
        headers,
        params,
        context: withCache(cacheOptions),
      })
      .pipe(map((response) => this.mapAccountResponse(response)));
  }

  public searchByLogon(logon: string): Observable<AccountResponse> {
    const shouldReset = logon?.trim();
    const params =
      shouldReset === ""
        ? {}
        : new HttpParams({
            fromObject: {
              logon,
            },
          });

    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `logons-${logon}`,
      bucket: this.logonsBucket,
    };

    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader().append(
      "Invalidate-cache",
      "true",
    );

    return this._http
      .get<AccountResponse>(url, {
        headers,
        params,
        context: withCache(cacheOptions),
      })
      .pipe(map((response) => this.mapAccountResponse(response)));
  }

  public searchUsers(
    searchValue: string,
    searchType: "logon" | "email" | "name",
  ): Observable<AccountResponse> {
    const shouldReset = searchValue?.trim();

    let params: HttpParams;
    if (shouldReset === "") {
      params = new HttpParams();
    } else {
      const paramObject: any = {};

      switch (searchType) {
        case "logon":
          paramObject.logon = searchValue;
          break;
        case "email":
          paramObject.email = searchValue;
          break;
        case "name":
          paramObject.name = searchValue;
          break;
      }

      params = new HttpParams({ fromObject: paramObject });
    }

    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `search-${searchType}-${searchValue}`,
      bucket: this.logonsBucket,
    };

    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader().append(
      "Invalidate-cache",
      "true",
    );

    return this._http
      .get<AccountResponse>(url, {
        headers,
        params,
        context: withCache(cacheOptions),
      })
      .pipe(map((response) => this.mapAccountResponse(response)));
  }

  public getUserMeInfo(): Observable<UserMe> {
    const headers = Utils.generateDefaultHeader();

    return this._http.get<UserMe>(
      `${environment.logonPortalBaseUrl}/user-accounts/me`,
      {
        headers,
      },
    );
  }

  public getMyAudits(params?: Map<string, string>): Observable<IMyAudit> {
    let url = `${environment.logonPortalBaseUrl}/my-audits`;
    let headers = Utils.generateDefaultHeader();

    const cacheOptions = {
      ttl: this.cacheExpiration,
      key: `logons-myAudits-${params?.get("offset")}`,
      bucket: this.logonsBucket,
    };

    if (params) {
      const queryString = Utils.paramsToString(params);
      url = `${url}?${queryString}`;
    }

    return this._http.get<IMyAudit>(url, {
      headers,
      context: withCache(cacheOptions),
    });
  }

  public invalidateLogonsCache(): void {
    this.bucket.delete(this.logonsBucket);
    this.clearPersistedCache();
  }

  /**
   * O cache do cashew é persistido no localStorage
   * (provideHttpCacheLocalStorageStrategy). Após uma troca de conta o app é
   * recarregado, então o `logonsBucket` em memória nasce vazio e não consegue
   * apagar as entradas antigas. Removemos aqui as chaves persistidas
   * diretamente para garantir que as listagens reflitam a conta autenticada.
   */
  private clearPersistedCache(): void {
    const cachePrefix = "@cache-";

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);

      if (key?.startsWith(cachePrefix)) {
        localStorage.removeItem(key);
      }
    }
  }

  private mapAccountResponse(response: AccountResponse): AccountResponse {
    return {
      ...response,
      logons: (response.logons ?? []).map((raw) =>
        this.mapLogonListItem(raw as RawLogonItem),
      ),
    };
  }

  private mapLogonListItem(raw: RawLogonItem): User {
    return {
      ...raw,
      isSystemic: mapIsSystemicFromAccessTypes(raw.accessTypes),
    };
  }

  private mapToCloneLogonSearchItems(logons: User[]): CloneLogonSearchItem[] {
    return logons
      .filter(
        (logon) => logon.status === "ACTIVE" && logon.userId && logon.logon,
      )
      .map((logon) => ({
        userId: logon.userId,
        name: logon.name,
        logon: logon.logon!,
        email: logon.email,
        profile: logon.profile ?? "",
        status: logon.status ?? "",
        mfaStatus: logon.mfaStatus ?? "UNKNOWN",
        displayLabel: `${logon.name ?? ""} - ${logon.email ?? ""} - ${
          logon.logon
        }`,
        displaySubLabel: `${logon.email ?? ""} - ${logon.logon}`,
      }));
  }

  public searchCloneLogonByNameEmailLogon(
    searchValue: string,
    searchType: "logon" | "email" | "name",
  ): Observable<CloneLogonSearchItem[]> {
    const trimmed = searchValue?.trim();
    if (!trimmed) {
      return of([]);
    }

    const paramObject: Record<string, string> = {
      isMaster: "true",
      offset: "0",
      limit: "20",
    };

    switch (searchType) {
      case "logon":
        paramObject["logon"] = trimmed;
        break;
      case "email":
        paramObject["email"] = trimmed;
        break;
      case "name":
        paramObject["name"] = trimmed;
        break;
    }

    const params = new HttpParams({ fromObject: paramObject });
    const url = `${environment.logonPortalBaseUrl}/user-accounts/me/logons`;
    const headers = Utils.generateDefaultHeader().append(
      "Invalidate-cache",
      "true",
    );

    return this._http
      .get<AccountResponse>(url, { headers, params })
      .pipe(
        map((response) =>
          this.mapToCloneLogonSearchItems(
            this.mapAccountResponse(response).logons ?? [],
          ),
        ),
      );
  }
}
